import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { X } from "lucide-react";
import { useSelector } from "react-redux";
import { UpdateUserProfile, useAppDispatch } from "../../../redux";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import Spinner from "../../shared/components/Spinner";
import { RoleEnum } from "../../shared/model";
import CategorySelection from "../components/ProfessionalProfileComponents/CategorySelection";
import ServiceAreaSelection from "../components/ProfessionalProfileComponents/ServiceAreaSelection";
import { getUserCategoriesByType, saveUserCategories } from "../../../redux/Actions/UserCategoryActions";
import { getTypeCategories } from "../../../redux/Actions/ClientActions";
import { UserCategoryTypeEnum } from "../../../redux/Slices/userCategorySlice";
import { ServiceArea } from "../../admin/models/ServiceArea.model";
import LocationComponent from "../../client/components/ClientProfileComponents/LocationComponent";
import { GlobalService } from "../../shared";
import ProfessionalRequirementsDialog from "../../../components/ui/ProfessionalRequirementsDialog";
import AIBioGenerator from "../../../components/ui/AIBioGenerator";
import { useTranslation } from "react-i18next";
interface EditProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    profileData: any;
    setProfileData: any;
    onDataSaved?: () => void; // Callback to notify parent when data is saved
    isAdminEdit?: boolean; // Whether this modal is being used by admin to edit another user
    adminUserId?: any; // The ID of the user being edited by admin
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, profileData, setProfileData, onDataSaved, isAdminEdit = false, adminUserId = null }) => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        name: profileData?.name || "",
        title: profileData?.title || "",
        email: profileData?.email || "",
        phone: profileData?.phone || "",
        bio: profileData?.bio || "",
        hourlyRate: profileData?.hourlyRate?.toString() || "",
        taxCode: profileData?.taxCode || "",
        // Location fields (for LocationComponent)
        address: profileData?.address || profileData?.personal?.address || "",
        latitude: profileData?.latitude || profileData?.personal?.latitude || "",
        longitude: profileData?.longitude || profileData?.personal?.longitude || "",
        province: profileData?.province || profileData?.personal?.province || "",
        country: profileData?.country || profileData?.personal?.country || undefined as any,
    });
    const { availabilities, emergencyAvailabilities } = useSelector((state: any) => state.professional);

    // Category selection state
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]); // Store full category data
    const [categoryResetKey, setCategoryResetKey] = useState(0); // Add reset key for CategorySelection

    // Debug: Log selectedCategories changes
    useEffect(() => {
        console.log("selectedCategories changed:", selectedCategories);
    }, [selectedCategories]);

    // Service area selection state
    const [selectedServiceAreas, setSelectedServiceAreas] = useState<ServiceArea[]>([]);
    const isLoading = useSelector(selectLoaderValue);
    const { user } = useSelector((state: any) => state.auth);
    const { userCategories } = useSelector((state: any) => state.userCategory);
    const categoriesByType = useSelector((state: any) => state.client.categoriesByType);
    const dispatch = useAppDispatch();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const getInitialType = () => {
        // If in admin mode, try to get the target user's role from profileData or default to professional
        if (isAdminEdit && profileData?.role?.id) {
            const validTypes = [RoleEnum.professional, RoleEnum.internationl_professional];
            return validTypes.includes(Number(profileData.role.id)) ? Number(profileData.role.id) : RoleEnum.professional;
        }

        // Otherwise use current user's role
        const validTypes = [RoleEnum.professional, RoleEnum.internationl_professional];
        return validTypes.includes(Number(user?.role?.id)) ? Number(user?.role?.id) : RoleEnum.professional;
    };

    const [professionalType, setProfessionalType] = useState<number>(getInitialType());
    const [categoryType, setCategoryType] = useState<number>(1); // Default to Home Service

    // Location enforcement dialog
    const [showLocationDialog, setShowLocationDialog] = useState<boolean>(false);
    const [locationError, setLocationError] = useState<string>("");

    // Professional requirements dialog
    const [showProfessionalRequirementsDialog, setShowProfessionalRequirementsDialog] = useState<boolean>(false);
    const [professionalRequirementsAccepted, setProfessionalRequirementsAccepted] = useState<boolean>(true);

    // Update form data when profileData changes (especially important for admin mode)
    useEffect(() => {
        if (profileData) {
            setFormData(prev => ({
                ...prev,
                name: profileData?.name || "",
                title: profileData?.title || "",
                email: profileData?.email || "",
                phone: profileData?.phone || "",
                bio: profileData?.bio || "",
                hourlyRate: profileData?.hourlyRate?.toString() || "",
                taxCode: profileData?.taxCode || "",
                address: profileData?.address || profileData?.personal?.address || prev.address,
                latitude: profileData?.latitude || profileData?.personal?.latitude || prev.latitude,
                longitude: profileData?.longitude || profileData?.personal?.longitude || prev.longitude,
                province: profileData?.province || profileData?.personal?.province || prev.province,
                country: profileData?.country || profileData?.personal?.country || prev.country,
            }));
        }
    }, [profileData]);

    // Fetch user categories when modal opens
    useEffect(() => {
        const targetUserId = isAdminEdit ? adminUserId : user?.id;
        if (isOpen && targetUserId) {
            fetchUserCategories();
        }
    }, [isOpen, user?.id, isAdminEdit, adminUserId]);

    useEffect(() => {
        console.log("professionalType", professionalType);
        if (professionalType === RoleEnum.internationl_professional) {
            setCategoryType(3);
        }
    }, [professionalType]);

    const fetchUserCategories = async () => {
        const targetUserId = isAdminEdit ? adminUserId : user?.id;
        // Check if we already have the data in Redux state
        const cacheKey = `${targetUserId}-${UserCategoryTypeEnum.Profile}`;
        const cachedData = userCategories[cacheKey];
        console.log("aaa", cachedData);
        if (cachedData) {
            // Use cached data
            const categories = cachedData.categoryIds || [];
            const subCategories = cachedData.subCategories || [];
            const serviceAreas = cachedData.serviceAreaIds || [];

            // Set selected categories and subcategories with consistent structure
            setSelectedCategories(categories.map(cat => ({
                id: cat.id,
                name: cat.name,
                description: cat.description || "",
                subcategories: cat.subcategories || [],
                isEmergency: cat.isEmergency,
                isRequireVerification: cat.isRequireVerification,
                type: cat.type
            })));
            setSelectedSubcategories(subCategories.map((name) => ({
                id: name, // Use subcategory name as ID since backend doesn't provide separate IDs
                name,
                description: ""
            })));
            setSelectedServiceAreas(serviceAreas.map(area => ({
                id: area.id,
                name: area.name || "",
                province: area.province || "",
                country: {
                    id: (area.country as any)?.id || "",
                    name: (area.country as any)?.name || "",
                    code: (area.country as any)?.code || ""
                },
                type: { id: 1, name: "Default" },
                radius: 0,
                restrictions: [],
                cities: [],
                postalCodes: [],
                serviceTypes: [],
                emergencyServices: false
            })));

            // Don't update parent's profileData here - let MyProfile.tsx handle its own state
            return;
        }

        // Fetch from backend if not cached
        try {
            const response = await dispatch(getUserCategoriesByType({
                userId: targetUserId,
                type: UserCategoryTypeEnum.Profile
            })).unwrap();

            if (response) {
                // Convert backend data to component state
                const categories = response.categoryIds || [];
                const subCategories = response.subCategories || [];
                const serviceAreas = response.serviceAreaIds || [];

                // Store full category data for saving later
                setAvailableCategories(categories);

                // Set selected categories and subcategories
                setSelectedCategories(categories.map(cat => ({
                    id: cat.id,
                    name: cat.name,
                    description: cat.description || "",
                    subcategories: cat.subcategories || [],
                    isEmergency: cat.isEmergency,
                    isRequireVerification: cat.isRequireVerification,
                    type: cat.type
                })));
                setSelectedSubcategories(subCategories.map((name) => ({
                    id: name, // Use subcategory name as ID since backend doesn't provide separate IDs
                    name,
                    description: ""
                })));
                setSelectedServiceAreas(serviceAreas.map(area => ({
                    id: area.id,
                    name: area.name || "",
                    province: area.province || "",
                    country: {
                        id: (area.country as any)?.id || "",
                        name: (area.country as any)?.name || "",
                        code: (area.country as any)?.code || ""
                    },
                    type: { id: 1, name: "Default" },
                    radius: 0,
                    restrictions: [],
                    cities: [],
                    postalCodes: [],
                    serviceTypes: [],
                    emergencyServices: false
                })));

                // Don't update parent's profileData here - let MyProfile.tsx handle its own state
            }
        } catch (error) {
            console.error("Error fetching user categories:", error);
        }
    };


    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        const { name, title, email, phone, bio, hourlyRate, taxCode } = formData;

        if (!name.trim()) {
            newErrors.name = t("editProfileModal.nameRequired");
        } else {
            // Check if name contains both first and last name
            const nameParts = name.trim().split(" ");
            if (nameParts.length < 2 || nameParts[1] === "") {
                newErrors.name = t("editProfileModal.nameMinLength");
            }
        }
        if (!title.trim()) newErrors.title = t("editProfileModal.titleRequired");
        if (!email.trim()) newErrors.email = t("editProfileModal.emailRequired");
        else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = t("editProfileModal.emailInvalid");

        if (!phone.trim()) newErrors.phone = t("editProfileModal.phoneRequired");
        if (!bio.trim()) {
            newErrors.bio = t("editProfileModal.bioRequired");
        } else if (bio.length > 450) {
            newErrors.bio = t("editProfileModal.bioMaxLength");
        }
        if (!hourlyRate.trim()) {
            newErrors.hourlyRate = t("editProfileModal.hourlyRateRequired");
        } else if (isNaN(Number(hourlyRate)) || Number(hourlyRate) <= 0) {
            newErrors.hourlyRate = t("editProfileModal.hourlyRateInvalid");
        }
        if (!taxCode.trim()) newErrors.taxCode = t("editProfileModal.taxCodeRequired");

        // Check professional requirements acceptance
        if (!professionalRequirementsAccepted) {
            newErrors.professionalRequirements = t("editProfileModal.professionalRequirementsRequired");
        }

        return newErrors;
    };
    const handleProfessionalTypeChange = (typeId: number) => {
        console.log("Professional type changed to:", typeId);
        setProfessionalType(typeId);

        // Automatically set default category type based on professional type
        if (typeId === RoleEnum.professional) {
            // National Professional -> default to Home Service
            setCategoryType(1);
        } else if (typeId === RoleEnum.internationl_professional) {
            // International Professional -> default to International Service
            setCategoryType(3);
        }
        // clearProfileUserCategory();
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setSelectedServiceAreas([]);
        setCategoryResetKey(prev => prev + 1); // Increment reset key to clear search input
    };

    const handleCategoryTypeChange = (typeId: number) => {
        console.log("Category type changed to:", typeId);
        setCategoryType(typeId);
        // Clear selections when category type changes to avoid confusion
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setSelectedServiceAreas([]);

        // Fetch categories for the new type
        if (user?.id || isAdminEdit) {
            const targetUserId = isAdminEdit ? adminUserId : user?.id;
            dispatch(getTypeCategories({ id: typeId }));
        }
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Optional: live validation
        setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));
    };

    // For LocationComponent to update fields
    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };



    const handleSave = async () => {
        const validationErrors = validate();

        // Enforce location for local professionals without address (service area not required)
        const isLocalProfessional = professionalType === RoleEnum.professional;
        const hasUserAddress = Boolean(isAdminEdit ? profileData?.address : user?.address) || Boolean(formData.address);
        if (isLocalProfessional && !hasUserAddress) {
            // setLocationError("Please set your address to continue.");
            // setShowLocationDialog(true);
            // return;
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        if (!emergencyAvailabilities || emergencyAvailabilities.length === 0) {
            if (selectedCategories.length == 0 || selectedServiceAreas.length == 0) {
                GlobalService.showToast("error", t("editProfileModal.required"), t("editProfileModal.categoryRequired"));
                return;
            }
        }

        // ✅ No need to check errors here again
        const [firstName, ...rest] = formData.name.split(" ");
        const lastName = rest.join(" ");

        const payload = {
            email: formData.email,
            firstName: firstName || (isAdminEdit ? profileData?.firstName : user.firstName),
            lastName: lastName || (isAdminEdit ? profileData?.lastName : user.lastName),
            bio: formData.bio,
            hourlyRate: Number(formData.hourlyRate),
            title: formData.title,
            phone: formData.phone,
            taxCode: formData.taxCode,
            socialLinks: isAdminEdit ? (profileData?.socialLinks || [""]) : (user?.socialLinks || [""]),
            photo: {
                id: isAdminEdit ? (profileData?.photo?.id || "") : (user?.photo?.id || ""),
            },
            role: {
                id: professionalType.toString(), // Use the selected professional type
            },
            status: {
                id: isAdminEdit ? (profileData?.status?.id || user?.status?.id) : user?.status?.id,
            },
            // If phone number changed, set isPhoneVerified to false
            isPhoneVerified: formData.phone !== user?.phone ? false : user?.isPhoneVerified,
            // Location fields
            // address: formData.address,
            // latitude: formData.latitude,
            // longitude: formData.longitude,
            // province: formData.province,
            // country: formData.country?.id ? { id: formData.country.id } : undefined,
            // Categories data kept as before
            categories: selectedCategories.map(cat => ({ id: cat.id })),
            subcategories: selectedSubcategories.map(sub => ({ id: sub.id })),
        } as any;
        const updatedPersonalData = {
            email: formData.email,
            name: firstName + " " + lastName,
            title: formData.title,
            bio: formData.bio,
            hourlyRate: Number(formData.hourlyRate),
            phone: formData.phone,
            taxCode: formData.taxCode,
            // address: formData.address,
            // latitude: formData.latitude,
            // longitude: formData.longitude,
            // province: formData.province,
            // country: formData.country,
        };
        // Update only the personal data in parent state, not categories
        setProfileData(prev => ({
            ...prev,
            personal: {
                ...prev.personal,
                ...updatedPersonalData,
            },
        }));

        // Save user profile
        const targetUserId = isAdminEdit ? adminUserId : user.id;
        await dispatch(UpdateUserProfile({ id: targetUserId, data: payload }));



        // Save user category selections
        try {
            console.log("About to save categories. selectedCategories:", selectedCategories);
            console.log("About to save categories. selectedSubcategories:", selectedSubcategories);
            console.log("About to save categories. selectedServiceAreas:", selectedServiceAreas);
            if (selectedCategories.length > 0 || selectedServiceAreas.length > 0) {
                // Send minimal format with just category IDs and service area IDs
                const categoryData = {
                    userId: targetUserId,
                    type: UserCategoryTypeEnum.Profile,
                    categoryIds: selectedCategories.map(cat => ({ id: cat.id })),
                    subCategories: selectedSubcategories.map(sub => sub.name),
                    serviceAreaIds: selectedServiceAreas.map(area => ({ id: area.id }))
                };

                console.log("Saving user categories:", categoryData);
                console.log("Selected categories:", selectedCategories);
                console.log("Selected subcategories:", selectedSubcategories);
                console.log("Selected service areas:", selectedServiceAreas);

                const savedData = await dispatch(saveUserCategories({
                    userId: targetUserId,
                    type: UserCategoryTypeEnum.Profile,
                    categoryIds: selectedCategories.length > 0 ? selectedCategories.map(cat => ({ id: cat.id })) : [],
                    subCategories: selectedSubcategories.length > 0 ? selectedSubcategories.map(sub => sub.name) : [],
                    serviceAreaIds: selectedServiceAreas.length > 0 ? selectedServiceAreas.map(area => ({ id: area.id })) : []
                })).unwrap();

                // Clear emergency data to ensure profile data takes precedence
                await dispatch(saveUserCategories({
                    userId: targetUserId,
                    type: UserCategoryTypeEnum.Emergency,
                    categoryIds: [],
                    subCategories: [],
                    serviceAreaIds: []
                }));

                console.log("Profile categories and service areas saved successfully");
                console.log("Emergency categories and service areas cleared to ensure profile data precedence");
            }

        } catch (error) {
            console.error("Error saving user categories:", error);
        }
        // Notify parent component to refresh its data
        if (onDataSaved) {
            onDataSaved();
        }

        onClose();
    };

    const clearProfileUserCategory = async () => {
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setSelectedServiceAreas([]);
        const targetUserId = isAdminEdit ? adminUserId : user?.id;
        await dispatch(saveUserCategories({
            userId: targetUserId,
            type: UserCategoryTypeEnum.Profile,
            categoryIds: [],
            subCategories: [],
            serviceAreaIds: []
        }));
        setCategoryResetKey(prev => prev + 1); // Increment reset key to clear search input
    };
    console.log(user);

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 bg-black bg-opacity-30">
                <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <Dialog.Title className="text-lg font-semibold">{t("editProfileModal.modalTitle")}</Dialog.Title>
                        <button
                            onClick={() => {
                                if (!isLoading) {
                                    onClose();
                                }
                            }}
                            className="text-gray-500 hover:text-black">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Notification for missing phone number */}
                    {!user?.phone && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">
                                        {t("editProfileModal.completeProfile")}
                                    </h3>
                                    <div className="mt-1 text-sm text-blue-700">
                                        <p>{t("editProfileModal.completeProfileMessage")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Form Fields */}
                    <div className="space-y-4">
                        {[
                            { name: "name", label: t("editProfileModal.name"), type: "text", placeholder: t("editProfileModal.namePlaceholder") },
                            { name: "title", label: t("editProfileModal.jobTitle"), type: "text", placeholder: t("editProfileModal.titlePlaceholder") },
                            { name: "email", label: t("editProfileModal.email"), type: "email", placeholder: t("editProfileModal.emailPlaceholder") },
                            { name: "taxCode", label: t("editProfileModal.taxCode"), type: "text", placeholder: t("editProfileModal.taxCodePlaceholder") },
                            { name: "hourlyRate", label: t("editProfileModal.hourlyRate"), type: "text", placeholder: t("editProfileModal.hourlyRatePlaceholder") },
                        ].map(field => (
                            <div key={field.name}>
                                <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                                <input
                                    disabled={field.name === "email"} // Disable email field
                                    name={field.name}
                                    type={field.type}
                                    value={formData[field.name as keyof typeof formData]}
                                    onChange={handleChange}
                                    placeholder={field.placeholder}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                {errors[field.name] && (
                                    <p className="text-sm text-red-600 mt-1">{errors[field.name]}</p>
                                )}
                            </div>
                        ))}

                        {/* Phone Field with + prefix */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t("editProfileModal.phone")}</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-black-500 text-sm">+</span>
                                </div>
                                <input
                                    type="tel"
                                    value={formData.phone.replace(/^\+/, "")}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, "");
                                        if (value) {
                                            value = "+" + value;
                                        }
                                        setFormData({ ...formData, phone: value });
                                    }}
                                    placeholder={t("editProfileModal.phonePlaceholder")}
                                    className="mt-1 block w-full pl-8 pr-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            {errors.phone && (
                                <p className="text-sm text-red-600 mt-1">{errors.phone}</p>
                            )}
                        </div>
                        {/* Professional Type Selection */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t("editProfileModal.professionalType")}</label>
                            <div className="flex gap-6">
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        checked={professionalType === RoleEnum.professional}
                                        onChange={() => handleProfessionalTypeChange(RoleEnum.professional)}
                                    />
                                    <span className="ml-2">{t("editProfileModal.nationalProfessional")}</span>
                                </label>
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        checked={professionalType === RoleEnum.internationl_professional}
                                        onChange={() => handleProfessionalTypeChange(RoleEnum.internationl_professional)}
                                    />
                                    <span className="ml-2">{t("editProfileModal.internationalProfessional")}</span>
                                </label>
                            </div>
                        </div>

                        {/* Category Type Selection */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t("editProfileModal.categoryType")}</label>
                            <div className="flex gap-6">
                                {professionalType === RoleEnum.professional && (
                                    <>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                checked={categoryType === 1}
                                                onChange={() => handleCategoryTypeChange(1)}
                                            />
                                            <span className="ml-2">{t("editProfileModal.homeService")}</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                checked={categoryType === 2}
                                                onChange={() => handleCategoryTypeChange(2)}
                                            />
                                            <span className="ml-2">{t("editProfileModal.professionalService")}</span>
                                        </label>
                                    </>
                                )}
                                {professionalType === RoleEnum.internationl_professional && (
                                    <label className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            checked={categoryType === 3}
                                            onChange={() => handleCategoryTypeChange(3)}
                                        />
                                        <span className="ml-2">{t("editProfileModal.internationalService")}</span>
                                    </label>
                                )}
                            </div>
                        </div>
                        {/* Category Selection Component */}
                        {(!emergencyAvailabilities || emergencyAvailabilities.length === 0 || Number(user?.role?.id) === RoleEnum.internationl_professional) && (
                            <div className="mt-6">
                                <CategorySelection
                                    selectedCategories={selectedCategories}
                                    selectedSubcategories={selectedSubcategories}
                                    onCategoriesChange={setSelectedCategories}
                                    onSubcategoriesChange={setSelectedSubcategories}
                                    disabled={isLoading}
                                    professionalType={categoryType}
                                    resetKey={categoryResetKey}
                                />
                                {errors.categories && (
                                    <p className="text-sm text-red-600 mt-2">{errors.categories}</p>
                                )}
                            </div>
                        )}

                        {/* Note: We leave ServiceAreaSelection in the main form (optional) */}
                        {(!emergencyAvailabilities || emergencyAvailabilities.length === 0) && (
                            <div className="mt-6">
                                <ServiceAreaSelection
                                    selectedServiceAreas={selectedServiceAreas}
                                    professionalType={professionalType}
                                    onServiceAreasChange={setSelectedServiceAreas}
                                    disabled={isLoading}
                                />
                                {errors.serviceAreas && (
                                    <p className="text-sm text-red-600 mt-2">{errors.serviceAreas}</p>
                                )}
                            </div>
                        )}
                        {/* Bio */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">{t("editProfileModal.bio")}</label>
                                <AIBioGenerator
                                    userId={isAdminEdit ? adminUserId : user?.id}
                                    language={i18n.language}
                                    onBioGenerated={(bio) => {
                                        handleChange({ target: { name: "bio", value: bio } } as any);
                                        // Clear any existing bio error
                                        if (errors.bio) {
                                            setErrors(prev => ({
                                                ...prev,
                                                bio: ""
                                            }));
                                        }
                                    }}
                                    disabled={isLoading}
                                    className="text-sm"
                                />
                            </div>
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                placeholder={t("editProfileModal.bioPlaceholder")}
                                rows={3}
                                maxLength={450}
                                className="w-full border rounded-md p-2"
                            />
                            {errors.bio && <p className="text-sm text-red-600 mt-1">{errors.bio}</p>}
                        </div>
                    </div>

                    {/* Professional Requirements Checkbox */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                id="professionalRequirements"
                                checked={professionalRequirementsAccepted}
                                onChange={(e) => setProfessionalRequirementsAccepted(e.target.checked)}
                                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="professionalRequirements" className="text-sm text-gray-700 leading-relaxed">
                                {t("editProfileModal.professionalRequirements")}{" "}
                                <button
                                    type="button"
                                    onClick={() => setShowProfessionalRequirementsDialog(true)}
                                    className="text-blue-600 underline hover:text-blue-800 transition-colors">
                                    {t("editProfileModal.professionalRequirementsLink")}
                                </button>
                            </label>
                        </div>
                        {errors.professionalRequirements && (
                            <p className="text-sm text-red-600 mt-2">{errors.professionalRequirements}</p>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col sm:flex-row justify-end mt-6 gap-3">
                        <button
                            onClick={() => {
                                if (!isLoading) {
                                    onClose();
                                }
                            }}
                            className="px-4 py-2 border rounded-md w-full sm:w-auto">
                            {t("editProfileModal.cancel")}
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-800 flex justify-center items-center w-full sm:w-auto">
                            {isLoading ? (
                                <>
                                    <Spinner size={5} />
                                    <span className="ml-2">{t("editProfileModal.loading")}</span>
                                </>
                            ) : (
                                t("editProfileModal.saveChanges")
                            )}
                        </button>
                    </div>
                </Dialog.Panel>
            </div>

            {/* Location Required Dialog using LocationComponent */}
            <Dialog open={showLocationDialog} onClose={() => setShowLocationDialog(false)} className="fixed z-[60] inset-0 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen p-4 bg-black bg-opacity-40">
                    <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl max-h-[85vh] overflow-y-auto">
                        <LocationComponent
                            editingLocation={true}
                            isEditing={true}
                            startEditingLocation={() => { }}
                            editedProfile={{
                                address: formData.address,
                                latitude: formData.latitude,
                                longitude: formData.longitude,
                                province: formData.province,
                                country: formData.country,
                            }}
                            handleFieldChange={handleFieldChange as any}
                            validationPersonalErrors={{} as any}
                            profileData={{
                                personal: {
                                    address: formData.address,
                                    latitude: formData.latitude,
                                    longitude: formData.longitude,
                                    province: formData.province,
                                    country: formData.country,
                                }
                            } as any}
                            handleLocationSaveChanges={() => {
                                if (!formData.address) {
                                    setLocationError("Please select a valid address.");
                                    return;
                                }
                                setShowLocationDialog(false);
                            }}
                            setEditingLocation={setShowLocationDialog as any}
                            showEditIcon={false}
                            isCurrentUser={true}
                            canEdit={true}
                        />

                        {locationError && (
                            <p className="text-sm text-red-600 mt-2">{locationError}</p>
                        )}
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* Professional Requirements Dialog */}
            <ProfessionalRequirementsDialog
                isOpen={showProfessionalRequirementsDialog}
                onClose={() => setShowProfessionalRequirementsDialog(false)}
            />
        </Dialog>
    );
};

export default EditProfileModal;
